import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, withRouter } from "react-router-dom";
import CommunitySocialNav from "./CommunitySocialNav";
import CommunityCreatePostCard from "./CommunityCreatePostCard";
import CommunityFeedPanel from "./CommunityFeedPanel";
import CommunityPromoRail from "./CommunityPromoRail";
import CommunityProfilePanel from "./CommunityProfilePanel";
import {
  fetchCommunityFollows,
  fetchCommunityPublicProfile,
  fetchProfileTimeline,
  fetchSavedSocialPosts,
  fetchSocialFeed,
  fetchSocialProfile,
} from "./communitySocialApi";
import { fetchOrganizationDirectory } from "../Helper/organizations";
import { fetchCreatorDirectory } from "../Helper/creators";
import { fetchProjectDirectory } from "../Helper/projects";
import {
  resolveMediaUrl,
  handleMediaImgError,
} from "../Helper/mediaUrl";

const FEED_PAGE_SIZE = 20;

/** Profile wall — personal posts only (exclude entity-attributed items). */
function isPersonalPost(post) {
  if (!post) return false;
  if (post.organization || post.creator || post.project) return false;
  const identity = post.author && post.author.identity;
  if (identity && identity !== "user") return false;
  return true;
}

function parseView(search) {
  const params = new URLSearchParams(search || "");
  const profileHandle = (params.get("profile") || "").trim().toLowerCase();
  if (profileHandle) {
    return { view: "public_profile", profileHandle };
  }
  const view = (params.get("view") || "posts").toLowerCase();
  if (
    view === "following" ||
    view === "saved" ||
    view === "profile" ||
    view === "posts"
  ) {
    return { view, profileHandle: null };
  }
  return { view: "posts", profileHandle: null };
}

/**
 * Social page — desktop 3-column (nav | create+feed | promos).
 * Phase E Sprint 5.0: Following / Saved / Profile switch the primary content area
 * via ?view= (no hash scroll into the Activity feed).
 * Sprint 6.3: Profile timeline + public ?profile=handle timeline.
 */
function CommunitySocialLayout({ pageTitle = "Social", location }) {
  const route = useMemo(
    () => parseView(location && location.search),
    [location && location.search]
  );
  const view = route.view;
  const profileHandle = route.profileHandle;

  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [feedLoading, setFeedLoading] = useState(true);
  const [feedError, setFeedError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [feedMode, setFeedMode] = useState("ranked");
  const [rankingMeta, setRankingMeta] = useState(null);
  // Fixed exclude snapshot + page for chrono continuation after ranked exhaustion.
  const [chronoExcludeIds, setChronoExcludeIds] = useState([]);
  const [chronoPage, setChronoPage] = useState(1);
  const [savedPosts, setSavedPosts] = useState([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [savedError, setSavedError] = useState(false);
  const [follows, setFollows] = useState([]);
  const [followCounts, setFollowCounts] = useState({});
  const [followsLoading, setFollowsLoading] = useState(false);
  const [followsError, setFollowsError] = useState(false);

  const [timelineProfile, setTimelineProfile] = useState(null);
  const [timelinePosts, setTimelinePosts] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState(false);
  const [timelinePage, setTimelinePage] = useState(1);
  const [timelineHasMore, setTimelineHasMore] = useState(false);
  const [timelineLoadingMore, setTimelineLoadingMore] = useState(false);
  const [timelineReloadKey, setTimelineReloadKey] = useState(0);

  const [orgs, setOrgs] = useState([]);
  const [creators, setCreators] = useState([]);
  const [projects, setProjects] = useState([]);
  const [promoLoading, setPromoLoading] = useState(true);

  const navActiveKey =
    view === "public_profile"
      ? "profile"
      : view === "posts"
        ? "posts"
        : view;

  const reload = useCallback(() => {
    setPage(1);
    setHasMore(false);
    setReloadKey((k) => k + 1);
  }, []);

  const reloadTimeline = useCallback(() => {
    setTimelinePage(1);
    setTimelineHasMore(false);
    setTimelineReloadKey((k) => k + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setPromoLoading(true);
      const [orgRes, creatorRes, projectRes] = await Promise.all([
        fetchOrganizationDirectory({ per_page: 12 }),
        fetchCreatorDirectory({ per_page: 12 }),
        fetchProjectDirectory({ per_page: 8 }),
      ]);
      if (!cancelled) {
        setOrgs(orgRes.organizations || []);
        setCreators(creatorRes.creators || []);
        setProjects(projectRes.projects || []);
        setPromoLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    // My Feed only — never load ranked/personalized feed while on Profile.
    if (view !== "posts") {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setFeedLoading(true);
      setFeedError(false);
      setLoadingMore(false);
      setFeedMode("ranked");
      setChronoExcludeIds([]);
      setChronoPage(1);
      const [me, feed] = await Promise.all([
        fetchSocialProfile(),
        fetchSocialFeed({
          per_page: FEED_PAGE_SIZE,
          page: 1,
          mode: "ranked",
          context: "global",
        }),
      ]);
      if (cancelled) return;

      setProfile(me.profile);
      if (feed.error) {
        setPosts([]);
        setPage(1);
        setHasMore(false);
        setFeedMode("ranked");
        setRankingMeta(null);
        setChronoExcludeIds([]);
        setChronoPage(1);
        setFeedError(true);
        setFeedLoading(false);
        return;
      }

      const nextPosts = feed.posts || [];
      setPosts(nextPosts);
      setPage(1);
      const more = !!(feed.pagination && feed.pagination.has_more);
      const rankedExhausted = !!(
        feed.pagination && feed.pagination.ranked_exhausted
      );
      if (rankedExhausted && more) {
        setFeedMode("chronological");
        setChronoExcludeIds(
          nextPosts.map((p) => p && p.id).filter((id) => id != null)
        );
        setChronoPage(1);
      } else {
        setFeedMode("ranked");
        setChronoExcludeIds([]);
        setChronoPage(1);
      }
      setHasMore(more);
      setRankingMeta(feed.ranking || null);
      setFeedError(false);
      setFeedLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadKey, view]);

  // Keep profile available for nav / composer when not on My Feed.
  useEffect(() => {
    if (view === "posts" || profile) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const me = await fetchSocialProfile();
      if (!cancelled && me.profile) {
        setProfile(me.profile);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [view, profile]);

  const loadFollows = useCallback(async () => {
    setFollowsLoading(true);
    setFollowsError(false);
    const result = await fetchCommunityFollows();
    setFollowsLoading(false);
    if (result.error) {
      setFollows([]);
      setFollowCounts({});
      setFollowsError(true);
      return;
    }
    setFollows(result.follows || []);
    setFollowCounts(result.counts || {});
    setFollowsError(false);
  }, []);

  const loadSaved = useCallback(async () => {
    setSavedLoading(true);
    setSavedError(false);
    const result = await fetchSavedSocialPosts({ per_page: 30 });
    setSavedLoading(false);
    if (result.error) {
      setSavedPosts([]);
      setSavedError(true);
      return;
    }
    setSavedPosts(result.posts || []);
  }, []);

  useEffect(() => {
    const onFollowChanged = () => {
      setPage(1);
      setHasMore(false);
      setFeedMode("ranked");
      setChronoExcludeIds([]);
      setChronoPage(1);
      setReloadKey((k) => k + 1);
      loadFollows();
    };
    window.addEventListener("community-follow-changed", onFollowChanged);
    return () => {
      window.removeEventListener("community-follow-changed", onFollowChanged);
    };
  }, [loadFollows]);

  useEffect(() => {
    if (view === "following") {
      loadFollows();
    }
    if (view === "saved") {
      loadSaved();
    }
  }, [view, loadFollows, loadSaved, reloadKey]);

  // Profile timeline — own (?view=profile) or public (?profile=handle)
  useEffect(() => {
    if (view !== "profile" && view !== "public_profile") {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setTimelineLoading(true);
      setTimelineError(false);
      setTimelineLoadingMore(false);

      let targetProfile = null;
      if (view === "public_profile" && profileHandle) {
        const pub = await fetchCommunityPublicProfile(profileHandle);
        if (cancelled) return;
        if (pub.error || !pub.profile) {
          setTimelineProfile(null);
          setTimelinePosts([]);
          setTimelineHasMore(false);
          setTimelineError(true);
          setTimelineLoading(false);
          return;
        }
        targetProfile = pub.profile;
      } else {
        const me = profile || (await fetchSocialProfile()).profile;
        if (cancelled) return;
        targetProfile = me
          ? { ...me, is_owner: me.is_owner !== false }
          : null;
      }

      setTimelineProfile(targetProfile);
      const userId = targetProfile && targetProfile.id;
      if (!userId) {
        setTimelinePosts([]);
        setTimelineHasMore(false);
        setTimelineLoading(false);
        return;
      }

      const feed = await fetchProfileTimeline({
        user_id: userId,
        per_page: FEED_PAGE_SIZE,
        page: 1,
      });
      if (cancelled) return;
      if (feed.error) {
        setTimelinePosts([]);
        setTimelineHasMore(false);
        setTimelineError(true);
        setTimelineLoading(false);
        return;
      }
      setTimelinePosts((feed.posts || []).filter(isPersonalPost));
      setTimelinePage(1);
      setTimelineHasMore(!!(feed.pagination && feed.pagination.has_more));
      setTimelineError(false);
      setTimelineLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [view, profileHandle, profile, timelineReloadKey]);

  const appendChronoFeed = useCallback(
    async (excludeIds, pageNum) => {
      const exclude = (excludeIds || [])
        .map((id) => Number(id))
        .filter((id) => id > 0)
        .slice(0, 5000);
      const feed = await fetchSocialFeed({
        per_page: FEED_PAGE_SIZE,
        page: pageNum || 1,
        mode: "chronological",
        context: "global",
        exclude_ids: exclude,
      });
      if (feed.error) {
        return { ok: false, posts: [], hasMore: false };
      }
      const seen = new Set(exclude.map((id) => String(id)));
      const appended = (feed.posts || []).filter((p) => !seen.has(String(p.id)));
      const beHasMore = !!(feed.pagination && feed.pagination.has_more);
      return {
        ok: true,
        posts: appended,
        // Paginate the filtered chrono remainder; empty page means exhausted.
        hasMore: beHasMore && appended.length > 0,
      };
    },
    []
  );

  const beginChronoContinuation = useCallback((existingPosts) => {
    const ids = (existingPosts || [])
      .map((p) => p && p.id)
      .filter((id) => id != null);
    setChronoExcludeIds(ids);
    setChronoPage(1);
    setFeedMode("chronological");
    return ids;
  }, []);

  const handleLoadMore = useCallback(async () => {
    if (loadingMore || !hasMore || feedError) return;
    setLoadingMore(true);

    if (feedMode === "chronological") {
      const result = await appendChronoFeed(chronoExcludeIds, chronoPage);
      if (result.ok && result.posts.length) {
        setPosts((prev) => [...prev, ...result.posts]);
        setChronoPage((p) => p + 1);
        setHasMore(result.hasMore);
      } else {
        setHasMore(false);
      }
      setLoadingMore(false);
      return;
    }

    const nextPage = page + 1;
    const feed = await fetchSocialFeed({
      per_page: FEED_PAGE_SIZE,
      page: nextPage,
      mode: "ranked",
      context: "global",
    });

    if (feed.error) {
      setHasMore(false);
      setLoadingMore(false);
      return;
    }

    const more = !!(feed.pagination && feed.pagination.has_more);
    const rankedExhausted = !!(
      feed.pagination && feed.pagination.ranked_exhausted
    );
    let merged = posts;

    if (feed.posts && feed.posts.length) {
      const seen = new Set(posts.map((p) => String(p.id)));
      const appended = feed.posts.filter((p) => !seen.has(String(p.id)));
      merged = [...posts, ...appended];
      setPosts(merged);
      setPage(nextPage);
      if (feed.ranking) setRankingMeta(feed.ranking);
    }

    if (rankedExhausted && more) {
      const excludeIds = beginChronoContinuation(merged);
      if (!feed.posts || !feed.posts.length) {
        const result = await appendChronoFeed(excludeIds, 1);
        if (result.ok && result.posts.length) {
          setPosts((prev) => [...prev, ...result.posts]);
          setChronoPage(2);
          setHasMore(result.hasMore);
        } else {
          setHasMore(false);
        }
      } else {
        setHasMore(true);
      }
    } else if (feed.posts && feed.posts.length) {
      setHasMore(more);
    } else {
      setHasMore(false);
    }
    setLoadingMore(false);
  }, [
    loadingMore,
    hasMore,
    feedError,
    page,
    feedMode,
    posts,
    chronoExcludeIds,
    chronoPage,
    appendChronoFeed,
    beginChronoContinuation,
  ]);

  const handleTimelineLoadMore = useCallback(async () => {
    if (timelineLoadingMore || !timelineHasMore || timelineError) return;
    const userId = timelineProfile && timelineProfile.id;
    if (!userId) return;
    const nextPage = timelinePage + 1;
    setTimelineLoadingMore(true);
    const feed = await fetchProfileTimeline({
      user_id: userId,
      per_page: FEED_PAGE_SIZE,
      page: nextPage,
    });
    if (!feed.error && feed.posts && feed.posts.length) {
      setTimelinePosts((prev) => {
        const seen = new Set(prev.map((p) => String(p.id)));
        const appended = feed.posts
          .filter(isPersonalPost)
          .filter((p) => !seen.has(String(p.id)));
        return [...prev, ...appended];
      });
      setTimelinePage(nextPage);
      setTimelineHasMore(!!(feed.pagination && feed.pagination.has_more));
    } else {
      setTimelineHasMore(false);
    }
    setTimelineLoadingMore(false);
  }, [
    timelineLoadingMore,
    timelineHasMore,
    timelineError,
    timelinePage,
    timelineProfile,
  ]);

  const handlePosted = useCallback((post, meta = {}) => {
    if (!post) return;
    const personal = isPersonalPost(post);
    if (meta.rollback) {
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
      setTimelinePosts((prev) => prev.filter((p) => p.id !== post.id));
      return;
    }
    if (meta.replaceId) {
      setPosts((prev) => {
        const without = prev.filter(
          (p) => p.id !== meta.replaceId && p.id !== post.id
        );
        return [post, ...without];
      });
      if (personal) {
        setTimelinePosts((prev) => {
          const without = prev.filter(
            (p) => p.id !== meta.replaceId && p.id !== post.id
          );
          return [post, ...without];
        });
      }
      setFeedError(false);
      return;
    }
    if (meta.optimistic) {
      setPosts((prev) => [post, ...prev]);
      // Profile wall only accepts personal-capacity posts.
      if (personal) {
        setTimelinePosts((prev) => [post, ...prev]);
      }
      setFeedError(false);
    }
  }, []);

  const handleItemUpdate = useCallback((updated) => {
    if (updated && updated._deleted) {
      setPosts((prev) => prev.filter((p) => String(p.id) !== String(updated.id)));
      setTimelinePosts((prev) =>
        prev.filter((p) => String(p.id) !== String(updated.id))
      );
      return;
    }
    setPosts((prev) =>
      prev.map((p) => (String(p.id) === String(updated.id) ? { ...p, ...updated } : p))
    );
    setTimelinePosts((prev) =>
      prev.map((p) =>
        String(p.id) === String(updated.id) ? { ...p, ...updated } : p
      )
    );
  }, []);

  const renderMain = () => {
    if (view === "profile" || view === "public_profile") {
      const panelProfile =
        view === "public_profile" ? timelineProfile : timelineProfile || profile;
      const isPublic = view === "public_profile" && !(panelProfile && panelProfile.is_owner);
      return (
        <CommunityProfilePanel
          profile={panelProfile}
          onProfileUpdate={(next) => {
            setProfile(next);
            setTimelineProfile(next);
          }}
          readOnly={isPublic}
          timelineItems={timelinePosts}
          timelineLoading={timelineLoading}
          timelineError={timelineError}
          timelineHasMore={timelineHasMore}
          timelineLoadingMore={timelineLoadingMore}
          onTimelineRetry={reloadTimeline}
          onTimelineLoadMore={handleTimelineLoadMore}
          onTimelineItemUpdate={handleItemUpdate}
        />
      );
    }

    if (view === "following") {
      return (
        <div className="community-social__feed-wrap">
          <div className="community-social__feed-head">
            <h2 className="community-social__feed-title">Following</h2>
            <p className="community-social__feed-note">
              Members, creators, organizations, and projects you follow
              {followCounts.organization ||
              followCounts.creator ||
              followCounts.project ||
              followCounts.user
                ? ` · ${(followCounts.organization || 0) + (followCounts.creator || 0) + (followCounts.project || 0) + (followCounts.user || 0)} total`
                : ""}
            </p>
          </div>
          {followsLoading ? (
            <p role="status">Loading follows…</p>
          ) : followsError ? (
            <div className="community-feed community-feed--error" role="alert">
              <h3 className="community-feed__heading">Couldn’t load following</h3>
              <p className="community-feed__body">
                Your follow list could not be loaded. Please try again.
              </p>
              <div className="community-feed__actions">
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => loadFollows()}
                >
                  Retry
                </button>
              </div>
            </div>
          ) : follows.length === 0 ? (
            <div className="community-feed community-feed--empty">
              <h3 className="community-feed__heading">Not following anyone yet</h3>
              <p className="community-feed__body">
                Follow members, organizations, creators, and projects to personalize your feed.
              </p>
              <div className="community-feed__actions">
                <Link to="/community/organizations" className="btn btn-danger">
                  Browse organizations
                </Link>
              </div>
            </div>
          ) : (
            <ul className="community-follow-list">
              {follows.map((row) => {
                const type = row.followable_type;
                const id = row.followable_id;
                const typeLabel =
                  row.type_label ||
                  (type === "organization"
                    ? "Organization"
                    : type === "creator"
                      ? "Creator"
                      : type === "user"
                        ? "Member"
                        : "Project");
                const name =
                  row.display_name ||
                  row.name ||
                  typeLabel;
                const avatar = row.avatar || row.logo_path || null;
                const path =
                  row.path ||
                  (type === "organization" && row.slug
                    ? `/organization/${row.slug}`
                    : type === "creator" && row.slug
                      ? `/creator/${row.slug}`
                      : type === "project" && row.slug
                        ? `/project/${row.slug}`
                        : type === "user" && row.slug
                          ? `/community/social?profile=${row.slug}`
                          : null);
                return (
                  <li key={`${type}-${id}`} className="community-follow-list__item">
                    <div className="community-follow-list__identity">
                      <span
                        className="community-follow-list__avatar"
                        aria-hidden="true"
                      >
                        {avatar ? (
                          <img
                            src={resolveMediaUrl(avatar)}
                            alt=""
                            onError={handleMediaImgError}
                          />
                        ) : (
                          (name || "?").charAt(0).toUpperCase()
                        )}
                      </span>
                      <div className="community-follow-list__text">
                        {path ? (
                          <Link
                            to={path}
                            className="community-follow-list__name"
                          >
                            {name}
                          </Link>
                        ) : (
                          <span className="community-follow-list__name">{name}</span>
                        )}
                        <span className="community-follow-list__type">
                          {typeLabel}
                        </span>
                      </div>
                    </div>
                    {path ? (
                      <Link
                        to={path}
                        className="btn btn-outline-secondary community-follow-list__open is-following"
                        aria-label={`Open ${name}`}
                      >
                        Following
                      </Link>
                    ) : (
                      <span className="community-follow-list__open is-following is-static">
                        Following
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      );
    }

    if (view === "saved") {
      return (
        <div className="community-social__feed-wrap">
          <div className="community-social__feed-head">
            <h2 className="community-social__feed-title">Saved Posts</h2>
          </div>
          <CommunityFeedPanel
            featureType="social"
            variant="posts"
            contextType="saved"
            title="Saved posts"
            loading={savedLoading}
            error={savedError}
            items={savedPosts}
            onRetry={loadSaved}
            onItemUpdate={(updated) => {
              setSavedPosts((prev) =>
                prev
                  .map((p) =>
                    String(p.id) === String(updated.id) ? { ...p, ...updated } : p
                  )
                  .filter((p) => p.is_saved !== false)
              );
              handleItemUpdate(updated);
            }}
            showRankingMeta={false}
          />
        </div>
      );
    }

    // Default: My Feed + composer
    return (
      <>
        <CommunityCreatePostCard
          profile={profile}
          onProfileUpdate={setProfile}
          onPosted={handlePosted}
        />
        <div className="community-social__feed-wrap">
          <div className="community-social__feed-head">
            <h2 className="community-social__feed-title">My Feed</h2>
            {rankingMeta && !feedError ? (
              <p className="community-social__feed-note">
                Your posts plus people and entities you follow — discovery and
                featured posts are clearly labeled.
              </p>
            ) : null}
          </div>
          <CommunityFeedPanel
            featureType="social"
            variant="posts"
            contextType="global"
            title={null}
            loading={feedLoading}
            error={feedError}
            items={posts}
            onRetry={reload}
            onItemUpdate={handleItemUpdate}
            showRankingMeta={!feedError}
            hasMore={hasMore}
            loadingMore={loadingMore}
            onLoadMore={handleLoadMore}
          />
        </div>
      </>
    );
  };

  return (
    <div className="community-social">
      <div className="community-social__grid">
        <aside className="community-social__col community-social__col--nav">
          <CommunitySocialNav activeKey={navActiveKey} profile={profile} />
        </aside>

        <div className="community-social__col community-social__col--main">
          {renderMain()}
        </div>

        <div className="community-social__col community-social__col--promo">
          <CommunityPromoRail
            organizations={orgs}
            creators={creators}
            projects={projects}
            loading={promoLoading}
          />
        </div>
      </div>
    </div>
  );
}

export default withRouter(CommunitySocialLayout);
